import { useCallback, useState } from "react";
import { danas } from "@/lib/dan";
import { useAuth } from "@/context/AuthContext";
import { datum } from "@/lib/format";
import { errorMessage } from "@/lib/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon, type IconName } from "@/components/ui/icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/design-system";
import { Feedback, useResource } from "@/components/workflow/shared";
import {
  siteEquipmentApi,
  type EquipmentArticle,
  type EquipmentGroup,
  type EquipmentImportPreview,
  type EquipmentMovementKind,
  type EquipmentResult,
} from "@/lib/api/oprema";
import {
  Condition,
  Field,
  History,
  Ownership,
  PlaceStatus,
  PendingWriteOff,
  SaveErrors,
  UndoNotice,
  useEquipmentAccess,
  useEquipmentSave,
  EquipmentRouteButton as Link,
} from "@/components/site-equipment/shared";
import {
  EquipmentDetail,
  EquipmentList,
  equipmentGroups,
} from "@/components/site-equipment/EquipmentList";
import { EquipmentPlaces } from "@/components/site-equipment/EquipmentPlaces";
import {
  EquipmentDraft,
  useEquipmentDraftState,
  type EquipmentDraftCache,
} from "@/components/site-equipment/EquipmentDraft";
import { EquipmentHome } from "@/components/site-equipment/EquipmentHome";
import {
  EquipmentBack,
  useEquipmentNavigation,
} from "@/components/site-equipment/EquipmentNavigation";
import { EquipmentPlaceView } from "@/components/site-equipment/EquipmentPlaceView";
import { EquipmentPhotoLink } from "@/components/site-equipment/EquipmentPhotoLink";
import {
  EquipmentScanButton,
  EquipmentScanner,
} from "@/components/site-equipment/EquipmentScanner";
import { EquipmentLabels } from "@/components/site-equipment/EquipmentLabels";
import {
  MovementForm,
  TransferForm,
  missingTargets,
} from "@/components/site-equipment/EquipmentForms";

export function EquipmentNav() {
  const access = useEquipmentAccess();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items: {
    url: string;
    label: string;
    icon: IconName;
    visible: boolean;
  }[] = [
    {
      url: "/oprema/preseli",
      label: "Preseli",
      icon: "move",
      visible: access.move,
    },
    {
      url: "/oprema/skeniraj",
      label: "Skeniraj",
      icon: "scan",
      visible: access.read,
    },
    { url: "/oprema/mjesta", label: "Mjesta", icon: "mapPin", visible: true },
    {
      url: "/oprema/ukupno",
      label: "Ukupno",
      icon: "clipboard",
      visible: true,
    },
    { url: "/oprema/najam", label: "Najam", icon: "clock", visible: true },
    {
      url: "/oprema/artikli",
      label: "Artikli",
      icon: "folder",
      visible: access.catalogue,
    },
    { url: "/oprema/inventure", label: "Inventura", icon: "clipboard", visible: true },
    { url: "/oprema/zahtjevi", label: "Zatraži iz Baze", icon: "truck", visible: true },
    { url: "/oprema/zapisnici", label: "Zapisnici", icon: "file", visible: true },
    {
      url: "/oprema/naljepnice",
      label: "Naljepnice",
      icon: "file",
      visible: access.catalogue,
    },
    {
      url: "/oprema/uvoz",
      label: "Uvoz",
      icon: "upload",
      visible: access.catalogue,
    },
    {
      url: "/oprema/odobrenja",
      label: "Čeka potvrdu",
      icon: "checkCircle",
      visible: access.approve,
    },
  ];
  return (
    <nav aria-label="Oprema" className="equipment-nav">
      {items
        .filter((item) => item.visible)
        .map(({ url, label, icon }) => (
          <Button
            variant="outline"
            className={`equipment-nav-tile ${label === "Preseli" ? "equipment-nav-main" : ""}`}
            key={url}
            aria-label={label}
            aria-current={
              (
                url === "/oprema/mjesta"
                  ? pathname.startsWith(url)
                  : pathname === url
              )
                ? "page"
                : undefined
            }
            onClick={() => navigate(url)}
          >
            <Icon name={icon} size={24} />
            <b>{label}</b>
            <small>
              {(
                {
                  Preseli: "s mjesta na mjesto",
                  Skeniraj: "QR na alatu ili inventarni broj",
                  Naljepnice: "odaberi komade i ispiši QR",
                  Mjesta: "Baza, Radiona, gradilišta",
                  Ukupno: "sva oprema na jednom mjestu",
                  Artikli: "popis vrsta opreme",
                  Uvoz: "upiši iz Excela",
                  Najam: "povrat, produljenje i šteta",
                  Inventura: "popiši i riješi razlike",
                  "Zatraži iz Baze": "potrebe i slanje opreme",
                  Zapisnici: "najam, inventura i zatvaranje",
                } as Record<string, string>
              )[label] ?? "za upravu"}
            </small>
          </Button>
        ))}
    </nav>
  );
}
export function SiteEquipmentPage({
  screen = "home",
}: {
  screen?:
    | "home"
    | "source"
    | "scan"
    | "labels"
    | "overview"
    | "places"
    | "place"
    | "piece"
    | "articles"
    | "import"
    | "approvals";
}) {
  const access = useEquipmentAccess();
  const { id } = useParams();
  const navigate = useNavigate();
  if (!access.read) return <p role="alert">Nemate ovlast za pregled opreme.</p>;
  return (
    <div className="site-equipment">
      {screen === "home" ? (
        <>
          <PageHeader title="Oprema" />
          <EquipmentHome>
            <EquipmentNav />
          </EquipmentHome>
        </>
      ) : null}
      {!["home", "place", "piece"].includes(screen) && (
        <EquipmentBack
          crumbs={[
            {
              label:
                (
                  {
                    source: "Preseli",
                    scan: "Skeniraj",
                    labels: "Naljepnice",
                    overview: "Ukupno",
                    places: "Mjesta",
                    articles: "Artikli",
                    import: "Uvoz",
                    approvals: "Čeka potvrdu",
                  } as Record<string, string>
                )[screen] ?? "Oprema",
            },
          ]}
        />
      )}
      {screen === "home" ? null : screen === "source" ? (
        <EquipmentHome chooseSource />
      ) : screen === "places" ? (
        <EquipmentPlaces />
      ) : screen === "scan" ? (
        <section className="equipment-form" aria-label="Skeniraj opremu">
          <h2 className="text-xl font-bold">Skeniraj opremu</h2>
          <EquipmentScanner
            onSelect={(piece) => navigate(`/oprema/komadi/${piece.id}`)}
          />
        </section>
      ) : screen === "labels" ? (
        <EquipmentLabels />
      ) : screen === "place" ? (
        <EquipmentPlaceView key={id} placeId={id} />
      ) : screen === "piece" ? (
        <PieceView key={id} id={id!} />
      ) : screen === "articles" ? (
        <ArticlesView />
      ) : screen === "import" ? (
        <ImportView />
      ) : screen === "approvals" ? (
        <ApprovalsView />
      ) : (
        <OverviewView />
      )}
    </div>
  );
}
function OverviewView() {
  const access = useEquipmentAccess();
  const resource = useResource(
    "equipment-overview",
    useCallback(async (signal) => {
      const [data, missing] = await Promise.all([
        siteEquipmentApi.overview(signal),
        siteEquipmentApi.missing(signal),
      ]);
      return { ...data, missing };
    }, []),
  );
  const [filter, setFilter] = useState("");
  const navigation = useEquipmentNavigation();
  const detailId = navigation.params.get("detalj") ?? "";
  const setDetailId = (value: string) =>
    value ? navigation.open({ detalj: value }) : navigation.close(["detalj"]);
  return (
    <>
      <Feedback error={resource.error} />
      {resource.error && (
        <Button onClick={resource.reload}>Pokušaj ponovno</Button>
      )}
      {resource.loading && <p role="status">Učitavanje pregleda…</p>}
      {resource.data &&
        (() => {
          const data = resource.data;
          const articles = data.artikli.filter(
            (a) =>
              !filter ||
              data.stanje.some(
                (s) =>
                  s.artikl_id === a.id &&
                  (filter === "najam"
                    ? !!s.najam_id
                    : filter === "osteceno"
                      ? s.osteceno > 0
                      : filter === "u_radioni"
                        ? s.u_radioni > 0
                        : s.u_prijevozu > 0),
              ) ||
              data.komadi.some(
                (p) =>
                  p.artikl_id === a.id &&
                  (filter === "najam" ? !!p.najam_id : p.stanje === filter),
              ),
          );
          const total = (id: string, place?: string) =>
            data.stanje
              .filter(
                (s) => s.artikl_id === id && (!place || s.mjesto_id === place),
              )
              .reduce((n, s) => n + s.ispravno + s.osteceno + s.u_radioni, 0) +
            data.komadi.filter(
              (p) =>
                p.artikl_id === id &&
                (!place || p.mjesto_id === place) &&
                ![
                  "otpisano",
                  "nestalo",
                  "vraceno_najmodavcu",
                  "u_prijevozu",
                ].includes(p.stanje),
            ).length;
          const inTransit = (id: string) =>
            data.stanje
              .filter((s) => s.artikl_id === id)
              .reduce((n, s) => n + s.u_prijevozu, 0) +
            data.komadi.filter(
              (p) => p.artikl_id === id && p.stanje === "u_prijevozu",
            ).length;
          const pendingWriteOff = (id: string, place?: string) =>
            data.stanje
              .filter(
                (s) => s.artikl_id === id && (!place || s.mjesto_id === place),
              )
              .reduce((n, s) => n + (s.ceka_otpis ?? 0), 0) +
            data.komadi.filter(
              (p) =>
                p.artikl_id === id &&
                p.ceka_otpis &&
                (!place || p.mjesto_id === place),
            ).length;
          const ownership = (id: string, place?: string) => {
            const rows = data.stanje.filter(
              (s) => s.artikl_id === id && (!place || s.mjesto_id === place),
            );
            const pieces = data.komadi.filter(
              (p) =>
                p.artikl_id === id &&
                (!place || p.mjesto_id === place) &&
                ![
                  "otpisano",
                  "nestalo",
                  "vraceno_najmodavcu",
                  ...(place ? ["u_prijevozu"] : []),
                ].includes(p.stanje),
            );
            const count = (rented: boolean) =>
              rows
                .filter((s) => !!s.najam_id === rented)
                .reduce(
                  (n, s) =>
                    n +
                    s.ispravno +
                    s.osteceno +
                    s.u_radioni +
                    (place ? 0 : s.u_prijevozu),
                  0,
                ) + pieces.filter((p) => !!p.najam_id === rented).length;
            return count(true)
              ? `Najam ${count(true)} od ${count(false) + count(true)}`
              : "";
          };
          return (
            <>
              <h2 className="text-xl font-bold">Oprema ukupno</h2>
              <div className="equipment-actions">
                <EquipmentScanButton />
                {access.catalogue && (
                  <Link to="/oprema/naljepnice">Naljepnice</Link>
                )}
              </div>
              <div className="equipment-summary">
                {[
                  ["U prijevozu", data.plocice.u_prijevozu],
                  ["Čeka potvrdu uprave", data.plocice.ceka_potvrdu],
                  ["U radioni preko roka", data.plocice.radiona_preko_roka],
                  ["Nije popisano", data.plocice.nije_popisano],
                  ["Inventura istekla", data.plocice.inventura_istekla],
                ].map(([name, count]) => (
                  <div key={name}>
                    <span>{name}</span>
                    <b>{count}</b>
                  </div>
                ))}
              </div>
              {!!data.plocice.bez_potpisa && (
                <p className="equipment-error">
                  Bez potpisa: {data.plocice.bez_potpisa}
                </p>
              )}
              {data.plocice.nije_popisano > 0 && (
                <p>
                  Pregled obuhvaća upisano stanje. Neka mjesta još nisu
                  popisana.
                </p>
              )}
              <Field label="Prikaži">
                <select
                  aria-label="Prikaži"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="">Sve</option>
                  <option value="osteceno">Oštećeno</option>
                  <option value="u_radioni">U radioni</option>
                  <option value="u_prijevozu">U prijevozu</option>
                  <option value="najam">Najam</option>
                </select>
              </Field>
              <EquipmentList
                label="Oprema ukupno"
                items={articles.map((a) => ({
                  id: a.id,
                  name: a.naziv,
                  group: a.skupina,
                  count: total(a.id) + inTransit(a.id),
                  badges: (
                    <>
                      {data.stanje.some(
                        (r) => r.artikl_id === a.id && r.osteceno > 0,
                      ) ||
                      data.komadi.some(
                        (p) => p.artikl_id === a.id && p.stanje === "osteceno",
                      ) ? (
                        <Badge tone="bad">Oštećeno</Badge>
                      ) : null}
                      {data.stanje.some(
                        (r) => r.artikl_id === a.id && r.najam_id,
                      ) ||
                      data.komadi.some(
                        (p) => p.artikl_id === a.id && p.najam_id,
                      ) ? (
                        <Badge tone="info">Najam</Badge>
                      ) : null}
                      {inTransit(a.id) > 0 && (
                        <Badge tone="info">{inTransit(a.id)} u prijevozu</Badge>
                      )}
                      {data.stanje.some(
                        (r) => r.artikl_id === a.id && r.u_radioni > 0,
                      ) ||
                      data.komadi.some(
                        (p) => p.artikl_id === a.id && p.stanje === "u_radioni",
                      ) ? (
                        <Badge tone="warn">Radiona</Badge>
                      ) : null}
                      <PendingWriteOff quantity={pendingWriteOff(a.id)} />
                    </>
                  ),
                }))}
                onOpen={setDetailId}
              />
              {detailId && (
                <EquipmentDetail
                  title={
                    data.artikli.find((a) => a.id === detailId)?.naziv ??
                    "Artikl"
                  }
                  onClose={() => setDetailId("")}
                >
                  <strong className="text-2xl">
                    Ukupno {total(detailId) + inTransit(detailId)}
                  </strong>
                  <p>{ownership(detailId)}</p>
                  <PendingWriteOff quantity={pendingWriteOff(detailId)} />
                  <p>U prijevozu: {inTransit(detailId)}</p>
                  {data.mjesta.map((p) => (
                    <div className="equipment-card" key={p.id}>
                      <div className="equipment-row">
                        <b>{p.naziv}</b>
                        <strong>{total(detailId, p.id)}</strong>
                      </div>
                      <PlaceStatus place={p} />
                      <p>{ownership(detailId, p.id)}</p>
                      <PendingWriteOff
                        quantity={pendingWriteOff(detailId, p.id)}
                      />
                      <Link to={`/oprema/mjesta/${p.id}?artikl=${detailId}`}>
                        Otvori {p.naziv}
                      </Link>
                    </div>
                  ))}
                </EquipmentDetail>
              )}
              {!articles.length && <p>Nema opreme za odabrani prikaz.</p>}
              <section className="equipment-card" aria-label="Nestalo">
                <h3 className="text-lg font-bold">Nestalo</h3>
                {data.missing.map((m) => (
                  <article className="equipment-card" key={m.kretanje_id}>
                    <b>
                      {data.artikli.find((a) => a.id === m.artikl_id)?.naziv ??
                        "Artikl"}
                    </b>
                    <p>
                      {data.mjesta.find((p) => p.id === m.mjesto_id)?.naziv} ·
                      Preostalo: {m.kolicina_preostalo} · {datum(m.datum)}
                    </p>
                    {m.komad_id ? (
                      <Link
                        className="equipment-link"
                        to={`/oprema/komadi/${m.komad_id}`}
                      >
                        Otvori komad
                      </Link>
                    ) : (
                      <Link
                        className="equipment-link"
                        to={`/oprema/mjesta/${m.mjesto_id}`}
                      >
                        Pronađeno na mjestu
                      </Link>
                    )}
                  </article>
                ))}
                {!data.missing.length && <p>Nema neriješene nestale opreme.</p>}
              </section>
              <h3 className="text-lg font-bold">Mjesta</h3>
              {data.mjesta.map((p) => (
                <article className="equipment-card" key={p.id}>
                  <div className="equipment-row">
                    <Link
                      className="equipment-link"
                      to={`/oprema/mjesta/${p.id}`}
                    >
                      {p.naziv}
                    </Link>
                    <PlaceStatus place={p} />
                  </div>
                </article>
              ))}
            </>
          );
        })()}
    </>
  );
}
function PieceView({ id }: { id: string }) {
  const access = useEquipmentAccess();
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const resource = useResource(
    id,
    useCallback(
      async (signal) => {
        const [detail, places] = await Promise.all([
          siteEquipmentApi.piece(id, signal),
          siteEquipmentApi.places(signal, access.move || access.maintain),
        ]);
        const missing =
          detail.komad.stanje === "nestalo"
            ? await siteEquipmentApi.missing(signal)
            : [];
        return { ...detail, places, missing };
      },
      [id, access.move, access.maintain],
    ),
  );
  const navigation = useEquipmentNavigation();
  const requestedForm = navigation.params.get("radnja");
  const form = [
    "transfer",
    "ostecenje",
    "u_radionu",
    "iz_radione",
    "produljenje_roka",
    "otpis",
    "nestalo",
    "pronadeno",
  ].includes(requestedForm ?? "")
    ? (requestedForm as EquipmentMovementKind | "transfer")
    : null;
  const setForm = (value: EquipmentMovementKind | "transfer" | null) =>
    value ? navigation.open({ radnja: value }) : navigation.close(["radnja"]);
  const [operation, setOperation] = useState<{
    result: EquipmentResult;
    message: string;
  } | null>(null);
  const saved = (result: EquipmentResult, message: string) => {
    setOperation({
      result,
      message:
        result.status === "ceka_potvrdu" ? "Čeka potvrdu uprave." : message,
    });
    drafts.clear();
    setForm(null);
    resource.reload();
  };
  return (
    <>
      <UndoNotice
        operation={operation}
        onUndone={() => {
          setOperation(null);
          resource.reload();
        }}
      />
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje komada…</p>}
      {resource.data &&
        (() => {
          const d = resource.data,
            p = d.komad,
            active = ["ispravno", "osteceno"].includes(p.stanje),
            maintain = access.canMaintain(d.mjesto);
          return (
            <>
              <EquipmentBack
                crumbs={[
                  {
                    label: d.mjesto.naziv,
                    to: `/oprema/mjesta/${d.mjesto.id}`,
                  },
                  { label: p.inventarni_broj },
                ]}
              />
              <div className="equipment-card">
                <PageHeader
                  title={d.artikl.naziv}
                  meta={`${p.inventarni_broj} · Serijski broj: ${p.serijski_broj ?? "nije upisan"}`}
                  status={<Condition value={p.stanje} />}
                />
                <Link
                  className="equipment-link"
                  to={`/oprema/mjesta/${p.mjesto_id}`}
                >
                  {d.mjesto.naziv}
                </Link>
                <Ownership item={p} />
                {p.fotka && (
                  <EquipmentPhotoLink
                    id={p.fotka.split("/").at(-1)!}
                    label={d.artikl.naziv}
                  />
                )}
                {p.rok_povratka && (
                  <p
                    className={
                      p.rok_povratka < danas() ? "equipment-warning" : ""
                    }
                  >
                    Rok povratka: {datum(p.rok_povratka)}
                  </p>
                )}
                {p.napomena && <p>{p.napomena}</p>}
                <PendingWriteOff quantity={p.ceka_otpis} />
                {!form && (
                  <div className="equipment-actions">
                    {p.stanje === "ispravno" &&
                      !p.ceka_otpis &&
                      access.canSend(d.mjesto) && (
                        <Button onClick={() => setForm("transfer")}>
                          Preseli
                        </Button>
                      )}
                    {p.stanje === "ispravno" && access.damage && (
                      <Button
                        variant="outline"
                        onClick={() => setForm("ostecenje")}
                      >
                        Oštećenje
                      </Button>
                    )}
                    {active && maintain && (
                      <Button
                        variant="outline"
                        onClick={() => setForm("u_radionu")}
                      >
                        Pošalji u radionu
                      </Button>
                    )}
                    {p.stanje === "u_radioni" && maintain && (
                      <>
                        <Button onClick={() => setForm("iz_radione")}>
                          Vraćeno iz radione
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setForm("produljenje_roka")}
                        >
                          Produlji rok
                        </Button>
                      </>
                    )}
                    {(active || p.stanje === "u_radioni") &&
                      maintain &&
                      !p.najam_id &&
                      !p.ceka_otpis && (
                        <Button
                          variant="outline"
                          onClick={() => setForm("otpis")}
                        >
                          Otpiši
                        </Button>
                      )}
                    {active && maintain && !p.ceka_otpis && (
                      <Button
                        variant="outline"
                        onClick={() => setForm("nestalo")}
                      >
                        Nestalo
                      </Button>
                    )}
                    {p.stanje === "nestalo" && maintain && (
                      <Button onClick={() => setForm("pronadeno")}>
                        Pronađeno
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {form === "transfer" && (
                <EquipmentDraft key="transfer" cache={drafts} name="transfer">
                  <TransferForm
                    initialPiece={p.id}
                    data={{
                      mjesto: d.mjesto,
                      artikli: [d.artikl],
                      stanje: [],
                      komadi: [p],
                      stize: [],
                      upozorenja: [],
                    }}
                    places={d.places}
                    onSaved={saved}
                    onClose={() => setForm(null)}
                  />
                </EquipmentDraft>
              )}
              {form && form !== "transfer" && (
                <EquipmentDraft key={form} cache={drafts} name={form}>
                  <MovementForm
                    kind={form}
                    targets={
                      form === "pronadeno"
                        ? (() => {
                            const source = d.povijest.find(
                              (e) =>
                                e.vrsta === "nestalo" &&
                                e.izvrseno &&
                                e.status === "provedeno" &&
                                d.missing.some(
                                  (m) =>
                                    m.komad_id === p.id &&
                                    m.kretanje_id === e.id,
                                ),
                            );
                            return missingTargets(
                              d.missing.filter(
                                (m) =>
                                  m.komad_id === p.id &&
                                  m.kretanje_id === source?.id,
                              ),
                              [d.artikl],
                              [p],
                            );
                          })()
                        : [{ article: d.artikl, piece: p }]
                    }
                    place={d.mjesto}
                    places={d.places}
                    onSaved={saved}
                    onClose={() => setForm(null)}
                  />
                </EquipmentDraft>
              )}
              <h2 className="text-lg font-bold">Povijest</h2>
              <History events={d.povijest} places={d.places} />
            </>
          );
        })()}
    </>
  );
}
function ArticlesView() {
  const access = useEquipmentAccess();
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const resource = useResource(
    `articles:${access.catalogue}`,
    useCallback(
      (signal) =>
        access.catalogue
          ? siteEquipmentApi.articles(signal)
          : Promise.resolve([]),
      [access.catalogue],
    ),
  );
  const navigation = useEquipmentNavigation();
  const detailId = navigation.params.get("detalj") ?? "";
  const setDetailId = (value: string) =>
    value ? navigation.open({ detalj: value }) : navigation.close(["detalj"]);
  const editId = navigation.params.get("uredi");
  const editing =
    editId === "new"
      ? "new"
      : (resource.data?.find((a) => a.id === editId) ?? null);
  const setEditing = (value: EquipmentArticle | "new" | null) =>
    value
      ? navigation.open({
          uredi: value === "new" ? "new" : value.id,
          detalj: null,
        })
      : navigation.close(["uredi"]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = useResource(
    `article-history:${detailId}:${historyOpen}`,
    useCallback(
      (signal) =>
        detailId && historyOpen
          ? siteEquipmentApi.history(detailId, undefined, signal)
          : Promise.resolve([]),
      [detailId, historyOpen],
    ),
  );
  if (!access.catalogue)
    return <p role="alert">Nemate ovlast za uređivanje artikala.</p>;
  return (
    <>
      <h2 className="text-xl font-bold">Artikli</h2>
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje artikala…</p>}
      <Button onClick={() => setEditing("new")}>Novi artikl</Button>
      {resource.data?.length === 0 && (
        <div className="equipment-card">
          <b>Još nema nijednog artikla.</b>
          <p>Prvo dodaj vrstu opreme, npr. okvir skele.</p>
        </div>
      )}
      {editing && (
        <EquipmentDraft
          key={editing === "new" ? "new" : editing.id}
          cache={drafts}
          name={editing === "new" ? "new" : editing.id}
        >
          <ArticleForm
            key={editing === "new" ? "new" : editing.id}
            article={editing === "new" ? undefined : editing}
            onSaved={() => {
              drafts.clear();
              setEditing(null);
              resource.reload();
            }}
            onClose={() => setEditing(null)}
          />
        </EquipmentDraft>
      )}
      <EquipmentList
        label="Artikli"
        items={(resource.data ?? []).map((a) => ({
          id: a.id,
          name: a.naziv,
          group: a.skupina,
          subtitle: a.nacin === "komad" ? "Po komadu" : "Po količini",
          badges: !a.aktivna ? (
            <Badge tone="neutral">Neaktivno</Badge>
          ) : undefined,
        }))}
        onOpen={(id) => setDetailId(id)}
      />
      {detailId &&
        (() => {
          const article = resource.data?.find((a) => a.id === detailId);
          return (
            article && (
              <EquipmentDetail
                title={article.naziv}
                onClose={() => {
                  setDetailId("");
                  setHistoryOpen(false);
                }}
              >
                <p>
                  {equipmentGroups[article.skupina]} ·{" "}
                  {article.nacin === "komad" ? "Po komadu" : "Po količini"} ·{" "}
                  {article.jedinica}
                </p>
                <Button
                  onClick={() => {
                    setEditing(article);
                  }}
                >
                  Uredi: {article.naziv}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setHistoryOpen((old) => !old)}
                  aria-expanded={historyOpen}
                >
                  Povijest
                </Button>
                {historyOpen && (
                  <>
                    <Feedback error={history.error} />
                    {history.loading ? (
                      <p role="status">Učitavanje povijesti…</p>
                    ) : (
                      <History events={history.data ?? []} places={[]} />
                    )}
                  </>
                )}
              </EquipmentDetail>
            )
          );
        })()}
    </>
  );
}
function ArticleForm({
  article,
  onSaved,
  onClose,
}: {
  article?: EquipmentArticle;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useEquipmentDraftState<Omit<EquipmentArticle, "id">>(
    "article",
    article ?? {
      naziv: "",
      skupina: "skela",
      nacin: "kolicina",
      jedinica: "kom",
      m2_po_kom: null,
      nabavna_cijena: null,
      aktivna: true,
    },
  );
  const save = useEquipmentSave(onSaved);
  return (
    <form
      className="equipment-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save.run(async () => {
          await siteEquipmentApi.saveArticle(data, article?.id);
          return { id: "", vrijeme: "", ponistivo_do: "", status: "" };
        }, "");
      }}
    >
      <Field label="Naziv" error={save.errors.naziv}>
        <input
          aria-label="Naziv"
          required
          value={data.naziv}
          onChange={(e) => setData({ ...data, naziv: e.target.value })}
        />
      </Field>
      <Field label="Skupina">
        <select
          aria-label="Skupina"
          value={data.skupina}
          onChange={(e) =>
            setData({ ...data, skupina: e.target.value as EquipmentGroup })
          }
        >
          {[
            ["skela", "Skela"],
            ["oplata", "Oplata"],
            ["podupiraci", "Podupirači"],
            ["alat", "Alat"],
            ["ostalo", "Ostalo"],
          ].map(([v, n]) => (
            <option key={v} value={v}>
              {n}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Način">
        <select
          aria-label="Način"
          disabled={!!article}
          value={data.nacin}
          onChange={(e) =>
            setData({
              ...data,
              nacin: e.target.value as EquipmentArticle["nacin"],
            })
          }
        >
          <option value="kolicina">Po količini</option>
          <option value="komad">Po komadu</option>
        </select>
      </Field>
      <Field label="Jedinica">
        <input
          aria-label="Jedinica"
          required
          value={data.jedinica}
          onChange={(e) => setData({ ...data, jedinica: e.target.value })}
        />
      </Field>
      <Field label="m² po komadu (neobavezno)">
        <input
          aria-label="m² po komadu"
          inputMode="decimal"
          pattern="\d+(\.\d+)?"
          value={data.m2_po_kom ?? ""}
          onChange={(e) =>
            setData({ ...data, m2_po_kom: e.target.value || null })
          }
        />
      </Field>
      <Field label="Nabavna cijena (neobavezno)">
        <input
          aria-label="Nabavna cijena"
          inputMode="decimal"
          pattern="\d+(\.\d{1,2})?"
          value={data.nabavna_cijena ?? ""}
          onChange={(e) =>
            setData({ ...data, nabavna_cijena: e.target.value || null })
          }
        />
      </Field>
      <label className="equipment-check">
        <input
          type="checkbox"
          checked={data.aktivna}
          onChange={(e) => setData({ ...data, aktivna: e.target.checked })}
        />
        Aktivno
      </label>
      <SaveErrors {...save} />
      <Button type="submit" disabled={save.pending}>
        Spremi artikl
      </Button>
      <Button type="button" variant="outline" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}
function ImportView() {
  const access = useEquipmentAccess();
  const [preview, setPreview] = useState<EquipmentImportPreview | null>(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [operation, setOperation] = useState<{
    result: EquipmentResult;
    message: string;
  } | null>(null);
  const save = useEquipmentSave((result, message) => {
    setPreview(null);
    setOperation({ result, message });
  });
  if (!access.catalogue)
    return <p role="alert">Nemate ovlast za uvoz opreme.</p>;
  return (
    <>
      <h2 className="text-xl font-bold">Uvoz iz Excela</h2>
      <UndoNotice operation={operation} onUndone={() => setOperation(null)} />
      <Button
        variant="outline"
        onClick={() => {
          setError("");
          void siteEquipmentApi
            .template()
            .then((blob) => {
              const url = URL.createObjectURL(blob),
                a = document.createElement("a");
              a.href = url;
              a.download = "oprema-predlozak.xlsx";
              a.click();
              window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            })
            .catch((cause) =>
              setError(
                errorMessage(
                  cause,
                  "Predložak nije dostupan. Pokušajte ponovno.",
                ),
              ),
            );
        }}
      >
        Preuzmi predložak
      </Button>
      <p>
        Stupci: artikl, skupina, način, mjesto, količina ili inventarni broj,
        serijski broj, vlasnik/najmodavac. Uvoz se sprema tek nakon pregleda bez
        grešaka.
      </p>
      <label>
        Excel datoteka
        <input
          aria-label="Excel datoteka"
          type="file"
          accept=".xlsx"
          disabled={pending || save.pending}
          onChange={(e) => {
            const file = e.target.files?.[0];
            setPreview(null);
            setError("");
            if (!file) return;
            if (!file.name.toLowerCase().endsWith(".xlsx")) {
              setError("Odaberi Excel datoteku .xlsx.");
              return;
            }
            setPending(true);
            void siteEquipmentApi
              .preview(file)
              .then(setPreview)
              .catch((cause) =>
                setError(
                  errorMessage(
                    cause,
                    "Pregled uvoza nije uspio. Provjerite datoteku i pokušajte ponovno.",
                  ),
                ),
              )
              .finally(() => setPending(false));
          }}
        />
      </label>
      {pending && <p role="status">Provjera redaka…</p>}
      <Feedback error={error} />
      {preview && (
        <>
          <div className="equipment-mobile">
            {preview.retci.map((r) => (
              <article className="equipment-card" key={r.redak}>
                <b>
                  Redak {r.redak}: {r.artikl}
                </b>
                <p>
                  {r.mjesto} · {r.inventarni_broj ?? r.kolicina}
                </p>
                {r.greske.map((g) => (
                  <p role="alert" key={g}>
                    {g}
                  </p>
                ))}
              </article>
            ))}
          </div>
          <div className="equipment-desktop">
            <table className="equipment-table">
              <thead>
                <tr>
                  <th>Redak</th>
                  <th>Artikl</th>
                  <th>Mjesto</th>
                  <th>Količina / inventarni broj</th>
                  <th>Greške</th>
                </tr>
              </thead>
              <tbody>
                {preview.retci.map((r) => (
                  <tr key={r.redak}>
                    <td>{r.redak}</td>
                    <td>{r.artikl}</td>
                    <td>{r.mjesto}</td>
                    <td>{r.inventarni_broj ?? r.kolicina}</td>
                    <td>{r.greske.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            disabled={
              save.pending ||
              !preview.retci.length ||
              !preview.id ||
              preview.retci.some((r) => r.greske.length > 0)
            }
            onClick={() =>
              void save.run(
                () => siteEquipmentApi.import(preview.id),
                "Uvoz spremljen.",
              )
            }
          >
            Uvezi {preview.retci.length} redaka
          </Button>
        </>
      )}
      <SaveErrors {...save} />
    </>
  );
}
function ApprovalsView() {
  const { user } = useAuth();
  const access = useEquipmentAccess();
  const resource = useResource(
    `approvals:${access.approve}`,
    useCallback(
      async (signal) =>
        access.approve
          ? Promise.all([
              siteEquipmentApi.approvals(signal),
              siteEquipmentApi.articles(signal),
            ])
          : null,
      [access.approve],
    ),
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [operation, setOperation] = useState<{
    result: EquipmentResult;
    message: string;
  } | null>(null);
  const save = useEquipmentSave((result, message) => {
    setOperation({ result, message });
    resource.reload();
  });
  if (!access.approve)
    return <p role="alert">Nemate ovlast za potvrdu uprave.</p>;
  return (
    <>
      <h2 className="text-xl font-bold">Čeka potvrdu</h2>
      <UndoNotice
        operation={operation}
        onUndone={() => {
          setOperation(null);
          resource.reload();
        }}
      />
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje zahtjeva…</p>}
      {resource.data?.[0].map((event) => (
        <article className="equipment-card" key={event.id}>
          <b>
            {(
              {
                pocetno: "Popiši – smanjenje",
                otpis: "Otpis",
                nestalo: "Nestalo",
                iz_radione: "Vraćeno iz radione – otpis",
              } as Record<string, string>
            )[event.vrsta] ?? "Zahtjev za potvrdu"}{" "}
            · {resource.data?.[1].find((a) => a.id === event.artikl_id)?.naziv}
          </b>
          <p>
            {Math.abs(event.kolicina)} kom · {event.korisnik} · {event.datum}
          </p>
          <p>{event.razlog}</p>
          {event.user_id === user?.id && (
            <p>Zahtjev mora odobriti druga osoba</p>
          )}
          {event.fotke.map((p) => (
            <EquipmentPhotoLink key={p.id} id={p.id} label={p.naziv} />
          ))}
          <Field label="Razlog odluke">
            <textarea
              aria-label={`Razlog odluke: ${event.id}`}
              value={reasons[event.id] ?? ""}
              onChange={(e) =>
                setReasons((r) => ({ ...r, [event.id]: e.target.value }))
              }
            />
          </Field>
          <div className="equipment-actions">
            <Button
              disabled={
                event.user_id === user?.id ||
                save.pending ||
                !reasons[event.id]?.trim()
              }
              onClick={() =>
                void save.run(
                  () =>
                    siteEquipmentApi.approve(
                      event.id,
                      true,
                      reasons[event.id]!,
                    ),
                  "Potvrda uprave spremljena.",
                )
              }
            >
              Potvrdi
            </Button>
            <Button
              variant="outline"
              disabled={
                event.user_id === user?.id ||
                save.pending ||
                !reasons[event.id]?.trim()
              }
              onClick={() =>
                void save.run(
                  () =>
                    siteEquipmentApi.approve(
                      event.id,
                      false,
                      reasons[event.id]!,
                    ),
                  "Odbijanje spremljeno.",
                )
              }
            >
              Odbij
            </Button>
          </div>
        </article>
      ))}
      {resource.data?.[0].length === 0 && (
        <p>Nema zahtjeva koji čekaju potvrdu uprave.</p>
      )}
      <SaveErrors {...save} />
    </>
  );
}
